/* eslint-disable @typescript-eslint/no-explicit-any */
import { SerializedError } from '@reduxjs/toolkit';
import { BaseQueryFn } from '@reduxjs/toolkit/query/react';
import { AxiosRequestConfig } from 'axios';

import { isAxiosError } from './apiErrorResponseHelper';
import { deleteApi, getApi, postApi, putApi } from './makeAjaxRequest';

export type HelloSelfResponse<T> = {
    ok: boolean;
    data: T;
    error: string[];
};

export type QueryParams = { [key: string]: string | number };

export type AmplifyBaseQueryResponse<T> = Promise<HelloSelfResponse<T>>;

export type AmplifyBaseQueryError = {
    status: number;
    data: any;
};

export const amplifyBaseQuery =
    (
        _args: { baseUrl: string } = { baseUrl: '' }
    ): BaseQueryFn<
        {
            api: string;
            endpoint: string;
            method: AxiosRequestConfig['method'];
            data?: AxiosRequestConfig['data'];
            params?: AxiosRequestConfig['params'];
            mockResponse?: object;
        },
        object | undefined,
        AmplifyBaseQueryError
    > =>
    async ({ api, endpoint, method, data, params, mockResponse }) => {
        try {
            if (mockResponse) {
                return {
                    data: mockResponse,
                };
            }

            let result: { data: object | undefined } = { data: undefined };
            switch (method) {
                case 'GET':
                    result = await getApi(api, endpoint, params);
                    break;
                case 'POST':
                    result = await postApi(api, endpoint, data);
                    break;
                case 'PUT':
                    result = await putApi(api, endpoint, data);
                    break;
                case 'DELETE':
                    result = await deleteApi(api, endpoint);
            }

            return { data: result };
        } catch (error) {
            /**
             * This will either be AxiosError or SerializedError
             *
             * AxiosError will be the type used if the endpoint returns an unhappy response (i.e. 400, 500 etc)
             * SerializedError is an error that occurred somewhere in this code (i.e. something is broken)
             *
             */
            if (isAxiosError(error) && endpoint.endsWith('signedPhotoUrl') && error.code === 'ERR_BAD_REQUEST') {
                // Handle profiles without photos that will return HTTP404
                return { data: { presignedGetUrl: '' } };
            } else if (isAxiosError(error)) {
                return {
                    error: {
                        status: error.response?.status,
                        data: error.response?.data || error.message,
                    } as AmplifyBaseQueryError,
                };
            } else {
                // SerializedError
                const err = error as SerializedError;
                console.error(err);
                return {
                    error: err as AmplifyBaseQueryError,
                };
            }
        }
    };
